import { useRefetchAvailableSlots } from "@/app/hooks/api_hooks/process/useReFetchAvailableSlots";
import { useRefetchBookedSlots } from "@/app/hooks/api_hooks/process/useRefetchBookedSlots";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook";
import { useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { ISlotsState, toggleSlotsFilters } from "@/app_redux/reducers/slices/auth/slots_slice";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import NudgesContainer from "@/app/components/ui/Nudges/NudgesContainer";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import { HOME_SERVICE_ID } from "@/constants";
import { useFeatureAccess } from "@/app/hooks/useFeatureAccess";
import { shouldShowAhsBookingLinkCard } from "@/app/components/routes/auth_routes/SlotsScreen/AhsBookingLinkCard/utils";

interface ISlotsHeaderNavOptions {
  id: number;
  title: string;
  url: string;
  className: string;
  headerTitle: string;
  show: boolean;
  fetchFunc: () => void;
}

const SlotsHeaderComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { getRouteKey } = useRouter();
  const slotsState = useRouteInfo(getRouteKey("SLOTS", "id")).routeState.state as ISlotsState;
  const { isPageReadonly, filters, atHomePreviousFlow, rescheduleFlag } = slotsState;
  const isAhsBookingLinkView = shouldShowAhsBookingLinkCard(slotsState);
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const refetchBookedSlots = useRefetchBookedSlots();
  const refetchAvailableSlots = useRefetchAvailableSlots();
  const { petId, customerId } = routeState.state as ICustomerProfileState;
  const { execute: logClick } = useClickLogAPI();
  const vmsUserId = useAppSelector((state) => state.auth?.user?.email);

  // Front desk books everything as overbooking, so no Available / Booked / Overbooking pills
  const isFrontDeskOverbooking = useFeatureAccess("FRONT_DESK_OVERBOOKING") && !isPageReadonly;
  const [isSearchSlotButtonClick, setIsSearchSlotButtonClick] = useState(false);
  const [hasShownNudges, setHasShownNudges] = useState(false);

  const SLOTS_HEADER_NAV_OPTIONS: ISlotsHeaderNavOptions[] = [
    {
      id: 1,
      title: "Available Slots",
      show: !isAhsBookingLinkView,
      url: isPageReadonly ? getRouteKey("CHECK_AVAILABLE_SLOTS", "url") : getRouteKey("AVAILABLE_SLOTS", "url"),
      className: "rounded-l-md",
      headerTitle: "Clinic's Available Slots",
      fetchFunc: refetchAvailableSlots,
    },
    {
      id: 2,
      title: "Booked Slots",
      show: true,
      url: isPageReadonly ? getRouteKey("CHECK_BOOKED_SLOTS", "url") : getRouteKey("BOOKED_SLOTS", "url"),
      className: isPageReadonly ? "rounded-r-md" : "",
      headerTitle: "Clinic's Booked Slots",
      fetchFunc: refetchBookedSlots,
    },
    {
      id: 3,
      show: !isPageReadonly,
      title: "Overbooking Slots",
      url: getRouteKey("OVERBOOKING_SLOTS", "url"),
      className: "rounded-r-md",
      headerTitle: "Clinic's Overbooking Slots",
      fetchFunc: () => { },
    },
  ];
  const [selectedButton, setSelectedButton] = useState<ISlotsHeaderNavOptions>(SLOTS_HEADER_NAV_OPTIONS[0]);

  const validateConditionalFormFields = () => {
    const hasSelectedCity = Array.isArray(filters?.city_id)
      ? filters?.city_id?.length > 0
      : typeof filters?.city_id === "string"
        ? filters?.city_id !== ""
        : false;
    let isValid = false;
    if (
      hasSelectedCity ||
      filters?.clinic_ids?.length > 0 ||
      filters?.staff_ids?.length > 0 ||
      filters.location_details?.latitude !== "" ||
      filters.location_details?.longitude !== ""
    ) {
      isValid = true;
    }
    return isValid;
  };

  const handelClick = (nav: ISlotsHeaderNavOptions, route: string) => {
    logClick({
      event_type: "SLOTS_NAV_TAB_CLICK",
      scr_id: isPageReadonly ? "CHECK_SLOT" : "BOOKING_PAGE",
      client_id: customerId,
      pet_id: petId,
      vms_user_id: vmsUserId,
      cta_text: nav.title,
      timestamp: Date.now(),
    });
    const wasShown = localStorage.getItem("nudgesShown_bookings");
    const shouldShow = wasShown === null || wasShown === "false";
    if (shouldShow) {
      setHasShownNudges(true);
      localStorage.setItem("nudgesShown_bookings", "true");
    }
    setIsSearchSlotButtonClick(true);

    setSelectedButton(nav);

    if (nav.id === 3) {
      dispatch(
        toggleSlotsFilters({
          status: true,
        }),
      );
    } else {
      dispatch(
        toggleSlotsFilters({
          status: false,
        }),
      );
    }
    if (isPageReadonly) {
      navigate(`${route}`);
    } else {
      navigate(`${route}/${customerId}/${petId}`);
    }
    if (nav.fetchFunc) {
      if (!validateConditionalFormFields()) return;
      nav.fetchFunc();
    }
    // if (isPageReadonly) {
    //   navigate(`${route}`);
    // } else {

    // }
  };

  useEffect(() => {
    if (location.pathname.includes("booked")) {
      setSelectedButton(SLOTS_HEADER_NAV_OPTIONS[1]);
      return;
    }
    if (location.pathname.includes("available")) {
      setSelectedButton(SLOTS_HEADER_NAV_OPTIONS[0]);
      return;
    }
    if (location.pathname.includes("overbooking")) {
      setSelectedButton(SLOTS_HEADER_NAV_OPTIONS[2]);
      return;
    }
  }, [location.pathname]);

  const showReadOnlyNotice =
    selectedButton?.id === 1 &&
    filters?.service_type_id === HOME_SERVICE_ID &&
    !rescheduleFlag &&
    Boolean(atHomePreviousFlow);

  const alert_category_list = ["Vaccination", "Deworming", "Membership", "Grooming Add Ons"];

  return (
    <>
      {isSearchSlotButtonClick && hasShownNudges && (
        <NudgesContainer
          alert_category_list={alert_category_list}
          source="booking_journey"
          service_id=""
          sub_service_id=""
        />
      )}
      <div className=" flex items-center bg-white rounded-t-lg	px-3 py-2 ">
        <div className=" w-1/2	">
          <p className="  text-sm font-semibold text-black text-left ">{isFrontDeskOverbooking ? "Book Overbooking" : selectedButton?.headerTitle}</p>
        </div>

        <div className="w-1/2 flex-row items-center opacity-100 p-0 flex gap-4 justify-end">
          <div className="  flex-row items-start opacity-100 p-0 flex gap-2">
            {!isFrontDeskOverbooking && SLOTS_HEADER_NAV_OPTIONS?.map((nav) => {
              return (
                nav.show && (
                  <button
                    key={`${nav.id}:${nav.url}`}
                    className={`px-4 py-1.5     whitespace-nowrap flex rounded-md font-medium text-sm ${selectedButton?.id === nav.id
                        ? " text-[#000000] border-[#000000]  border-[1.5px]"
                        : "bg-[#F4F4F4] text-[#6C727F] border-[#E2E2E2]  border-[1px]"
                      }`}
                    onClick={() => handelClick(nav, nav.url)}
                  >
                    {nav.title}
                  </button>
                )
              );
            })}
          </div>
        </div>
      </div>
      {showReadOnlyNotice && (
        <div className="bg-white px-3 pb-2">
          <div className="flex items-start gap-2.5 rounded-[8px] border border-[#FCD34D] bg-[#FFFBEB] px-3 py-2.5">
            <ExclamationTriangleIcon className="mt-[1px] h-4 w-4 shrink-0 text-amber-500" />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold leading-tight text-amber-800">Read only view</p>
              <p className="mt-0.5 text-[12px] leading-tight text-amber-700">
                Slots cannot be selected while doctor wise slots are shown. Turn the toggle off to book.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default SlotsHeaderComponent;
